import { useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSuppliersSearch } from "@/api/hooks";
import type { Supplier, SupplierStatus, SupplierType } from "@/api/types";

const STATUS_LABELS: Record<SupplierStatus, string> = {
  active: "Actif",
  inactive: "Inactif",
  suspended: "Suspendu",
};

const STATUS_COLORS: Record<SupplierStatus, string> = {
  active: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  inactive: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
  suspended: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

const TYPE_LABELS: Record<SupplierType, string> = {
  publisher: "Editeur",
  distributor: "Distributeur",
  bookstore: "Librairie",
  online: "En ligne",
  other: "Autre",
};

export function SuppliersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputValue, setInputValue] = useState(searchParams.get("q") ?? "");

  const query = searchParams.get("q") ?? "";
  const statusFilter = searchParams.get("status") as SupplierStatus | null;

  const {
    data: suppliers,
    isLoading,
    error,
  } = useSuppliersSearch(query || undefined, statusFilter || undefined);

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const params = new URLSearchParams(searchParams);
      if (inputValue.trim()) {
        params.set("q", inputValue.trim());
      } else {
        params.delete("q");
      }
      setSearchParams(params);
    },
    [inputValue, searchParams, setSearchParams]
  );

  const handleStatusFilter = useCallback(
    (status: SupplierStatus | null) => {
      const params = new URLSearchParams(searchParams);
      if (status) {
        params.set("status", status);
      } else {
        params.delete("status");
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Fournisseurs</h1>
        <Link
          to="/acquisition/suppliers/new"
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
        >
          <span>+</span>
          Nouveau fournisseur
        </Link>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="flex gap-3">
        <div className="relative flex-1">
          <input
            type="search"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
            }}
            placeholder="Rechercher par nom, code, email..."
            className="w-full rounded-lg border border-gray-300 px-4 py-3 pl-10 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
          <svg
            className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <button
          type="submit"
          className="rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:outline-none"
        >
          Rechercher
        </button>
      </form>

      {/* Status Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => {
            handleStatusFilter(null);
          }}
          className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
            !statusFilter
              ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
          }`}
        >
          Tous
        </button>
        {(["active", "inactive", "suspended"] as SupplierStatus[]).map((status) => (
          <button
            key={status}
            onClick={() => {
              handleStatusFilter(status);
            }}
            className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
              statusFilter === status
                ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
            }`}
          >
            {STATUS_LABELS[status]}
          </button>
        ))}
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      ) : error ? (
        <div className="rounded-lg bg-red-100 p-4 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
          Une erreur est survenue lors de la recherche.
        </div>
      ) : suppliers && suppliers.length > 0 ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {suppliers.length} fournisseur{suppliers.length > 1 ? "s" : ""}
          </p>
          <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
            {suppliers.map((supplier) => (
              <SupplierRow key={supplier.id} supplier={supplier} />
            ))}
          </div>
        </div>
      ) : query || statusFilter ? (
        <div className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">Aucun fournisseur trouvé</p>
        </div>
      ) : (
        <div className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            Entrez un terme de recherche ou ajoutez un fournisseur
          </p>
        </div>
      )}
    </div>
  );
}

function SupplierRow({ supplier }: { supplier: Supplier }) {
  return (
    <Link
      to={`/acquisition/suppliers/${supplier.id}`}
      className="block p-4 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <h3 className="font-medium text-gray-900 dark:text-white">{supplier.name}</h3>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[supplier.status]}`}
            >
              {STATUS_LABELS[supplier.status]}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            <span>{supplier.code}</span>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <span>{TYPE_LABELS[supplier.type]}</span>
            {supplier.email && (
              <>
                <span className="text-gray-300 dark:text-gray-600">•</span>
                <span>{supplier.email}</span>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 text-sm">
          {supplier.city && (
            <span className="text-gray-500 dark:text-gray-400">{supplier.city}</span>
          )}
          <svg
            className="h-5 w-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </Link>
  );
}

export default SuppliersPage;
